import React, { useEffect, useRef, useState } from 'react'
import { Button, Input } from '@yuanfudao/metis-web-component'
import { validatePassword } from '@/utils/password'
import lessModule from './index.module.less'

const passwordHidden = 'https://miaoduo.fbcontent.cn/private/resource/image/1a139713bcd7c7b-f9c7f65c-7112-4ca7-8642-68040814fe4e.svg'

interface SetPasswordProps {
  onSubmit: (password: string) => Promise<void>
  onCancel: () => void
}

export default function SetPassword({ onSubmit, onCancel }: SetPasswordProps) {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [confirmationVisible, setConfirmationVisible] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [confirmationTouched, setConfirmationTouched] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const mounted = useRef(true)
  const pending = useRef(false)
  const completed = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const passwordError = passwordTouched && !validatePassword(password)
    ? (password ? '密码格式不正确，请按规则重新设置' : '请输入新密码') : ''
  const confirmationError = confirmationTouched && (!confirmation || password !== confirmation)
    ? (confirmation ? '两次输入的密码不一致，请重新输入' : '请再次输入新密码') : ''
  const valid = validatePassword(password) && password === confirmation

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid || pending.current || completed.current) return
    pending.current = true
    setBusy(true)
    setSubmitError('')
    try {
      await onSubmit(password)
      completed.current = true
      if (mounted.current) setSaved(true)
    } catch (cause: any) {
      if (mounted.current) setSubmitError(cause?.response?.data?.message || cause?.message || '密码设置失败，请稍后重试')
    } finally {
      pending.current = false
      if (mounted.current) setBusy(false)
    }
  }

  const visibility = (visible: boolean, toggle: () => void) => (
    <Button
      type="text"
      htmlType="button"
      className={lessModule.visibility}
      aria-label={visible ? '隐藏密码' : '显示密码'}
      aria-pressed={visible}
      onMouseDown={(event) => event.preventDefault()}
      onClick={toggle}
    >
      <img src={passwordHidden} alt="" />
    </Button>
  )

  return (
    <form className={`${lessModule.card} ${lessModule.setPasswordCard}`} onSubmit={submit} aria-labelledby="set-password-title">
      <h1 id="set-password-title">设置登录密码</h1>
      <p className={lessModule.description} id="set-password-rules">8-20位，数字、字母、符号的两种或以上组合</p>
      <Input
        className={lessModule.input}
        aria-label="新密码"
        aria-describedby="set-password-rules set-password-error"
        aria-invalid={Boolean(passwordError)}
        autoComplete="new-password"
        placeholder="请输入新密码"
        type={passwordVisible ? 'text' : 'password'}
        value={password}
        disabled={busy || saved}
        onChange={(event) => { setPassword(event.target.value); setSubmitError('') }}
        onBlur={() => setPasswordTouched(true)}
        suffix={visibility(passwordVisible, () => setPasswordVisible((visible) => !visible))}
      />
      <Input
        className={lessModule.input}
        aria-label="确认新密码"
        aria-describedby="set-password-error"
        aria-invalid={Boolean(confirmationError)}
        autoComplete="new-password"
        placeholder="再次输入新密码"
        type={confirmationVisible ? 'text' : 'password'}
        value={confirmation}
        disabled={busy || saved}
        onChange={(event) => { setConfirmation(event.target.value); setSubmitError('') }}
        onBlur={() => setConfirmationTouched(true)}
        suffix={visibility(confirmationVisible, () => setConfirmationVisible((visible) => !visible))}
      />
      <div className={lessModule.error} id="set-password-error" aria-live="polite">
        {submitError || passwordError || confirmationError}
      </div>
      <div className={lessModule.actions}>
        <Button type="bordered" htmlType="button" className={lessModule.cancel} disabled={busy} onClick={onCancel}>取消</Button>
        <Button type="primary" htmlType="submit" className={lessModule.submit} disabled={!valid || busy || saved}>
          {saved ? '重置成功' : busy ? '设置中...' : '下一步'}
        </Button>
      </div>
    </form>
  )
}
